import {
  AdminScheduleType,
  AdminScheduleUserImport
} from '@dadohr/shared/domainTypes/adminSchedules';
import { Doc } from '@dadohr/shared/domainTypes/document';
import {
  Integration,
  OrgIntegrations
} from '@dadohr/shared/domainTypes/integrations';
import {
  MergeHRISIntegration,
  MergeIntegration
} from '@dadohr/shared/domainTypes/integrations/merge';
import { CustomUserField, IOrganization } from '@dadohr/shared/domainTypes/org';
import {
  sortUserImportDataSources,
  UserImportDataSource,
  UserImportDataSourceAdpWorkforceNow,
  UserImportDataSourceAirtable,
  UserImportDataSourceAshby,
  UserImportDataSourceBambooHr,
  UserImportDataSourceBob,
  UserImportDataSourceChartHop,
  UserImportDataSourceEmailCsv,
  UserImportDataSourceGoogleSheet,
  UserImportDataSourceGreenhouseV3,
  UserImportDataSourceIdentifier,
  UserImportDataSourceMergeHRIS,
  UserImportDataSourceMSExcel,
  UserImportDataSourcePingboard,
  UserImportDataSourceSalesforce,
  UserImportDataSourceSftp,
  UserImportDataSourceUkg,
  UserImportDataSourceWorkdayReportApi,
  UserImportSourceType
} from '@dadohr/shared/domainTypes/user';
import { FS } from '@dadohr/shared/versions';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import {
  Button,
  Dialog,
  DialogContent,
  FormControlLabel,
  List,
  Link as MaterialUILink,
  Switch,
  Typography
} from '@material-ui/core';
import { sortBy } from 'lodash';
import { ExternalLink, Plus, RefreshCw, Settings } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCollection } from 'react-firebase-hooks/firestore';
import { Link } from 'react-router-dom';
import shortid from 'shortid';
import {
  ButtonLoader,
  ButtonWithPromise
} from '../../../../../components/ButtonWithPromise';
import { DialogActionsHeader } from '../../../../../components/DialogActionsHeader';
import { DialogActionsWithSlots } from '../../../../../components/DialogActionsWithSlots';
import { DialogTitleWithTypography } from '../../../../../components/DialogTitleWithTypography';
import { FrequencyEdit } from '../../../../../components/FrequencyEdit';
import { Loader } from '../../../../../components/Loader';
import { useDialogState } from '../../../../../hooks/useDialogState';
import { useErrorLogger } from '../../../../../hooks/useErrorLogger';
import { useUnsafeTranslation } from '../../../../../i18next';
import { FlexContainer } from '../../../../../layout/Flex';
import { useBooleanQueryParam, useRoutes } from '../../../../../routes';
import {
  toAdminScheduleDoc,
  updateOrCreateSchedule
} from '../../../../../services/adminSchedules';
import { store, useMappedLoadingValue } from '../../../../../services/firebase';
import { useIntegrationsContext } from '../../../../../services/integrations/context';
import { now } from '../../../../../services/time';
import {
  startImport,
  toUserImportSourceIdentifier,
  useUserImportDataSources
} from '../../../../../services/user/import';
import { Functionality } from '../../../../Org/pages/Integrations';
import {
  IntegrationButton,
  useConfigInfos
} from '../../../../Org/pages/Integrations/integrations/common';
import {
  AdpWorkforceNowSetupFlow,
  ImportSourceRowAdpWorkForceNow
} from './sources/adpWorkforceNow';
import { AirtableSetupFlow, ImportSourceRowAirtable } from './sources/airtable';
import { AshbySetupFlow, ImportSourceRowAshby } from './sources/ashby';
import { BambooHrSetupFlow, ImportSourceRowBambooHr } from './sources/bamboohr';
import { BobSetupFlow, ImportSourceRowBob } from './sources/bob';
import { ChartHopSetupFlow, ImportSourceRowChartHop } from './sources/charthop';
import {
  EmailCsvSetupFlow,
  ImportSourceRowEmailCsv
} from './sources/emailCsv';
import {
  GreenhouseV3SetupFlow,
  ImportSourceRowGreenhouseV3
} from './sources/greenhouseV3';
import {
  ImportSourceRowMergeHRIS,
  MergeHRISSetupFlow
} from './sources/mergeHRIS';
import {
  ImportSourceIntegrationInfo,
  IntegrationsInfosMap,
  MergeHRISIntegrationsInfoMap
} from './sources/meta';
import { PersonioSetupFlow } from './sources/personio';
import {
  ImportSourceRowPingboard,
  PingboardSetupFlow
} from './sources/pingboard';
import {
  ImportSourceRowSalesforce,
  SalesforceSetupFlow
} from './sources/salesforce';
import { ImportSourceRowSftp, SftpSetupFlow } from './sources/sftp';
import {
  ImportSourceRowExcel,
  ImportSourceRowGoogleSheet,
  SpreadsheetSetupFlow,
  SpreadSheetType
} from './sources/spreadsheet';
import { ImportSourceRowUkg, UkgSetupFlow } from './sources/ukg';
import { ImportSourceRowWorkday, WorkdaySetupFlow } from './sources/workday';

const mergeHrisInfos = Object.values(MergeHRISIntegrationsInfoMap);
const staticInfos = Object.values(IntegrationsInfosMap);

const IMPORT_INTEGRATIONS: ImportSourceIntegrationInfo[] = mergeHrisInfos
  .concat(staticInfos);

const IntegrationsGrid = styled('div')((p) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
  gridColumnGap: p.theme.spacing(2),
  gridRowGap: p.theme.spacing(2)
}));

export const isActiveIntegration = (
  configInfo: OrgIntegrations,
  integration: Integration,
  mergeIntegration?: MergeIntegration,
  // Runtime active set from IntegrationsContext — fallback source-of-truth for
  // integrations whose backend onWrite trigger hasn't (yet) populated
  // configInfos[integration].active. Mirrors the same fix on the Integrations
  // page (Org/pages/Integrations/index.tsx). MERGE still requires configInfos
  // because it carries the activeHRIS array — the runtime set can't
  // substitute for that.
  activeIntegrationsSet?: ReadonlySet<Integration>
) => {
  if (integration === Integration.MERGE) {
    const activeHRIS = configInfo.configInfos[Integration.MERGE]?.activeHRIS;
    return (
      mergeIntegration && activeHRIS && activeHRIS.includes(mergeIntegration)
    );
  }
  return (
    configInfo.configInfos[integration]?.active ||
    activeIntegrationsSet?.has(integration) ||
    false
  );
};

const Content = styled('div')((p) => ({
  display: 'flex',
  flexDirection: 'column',
  paddingTop: p.theme.spacing(7),
  paddingBottom: p.theme.spacing(7),
  paddingLeft: p.theme.spacing(3),
  paddingRight: p.theme.spacing(3)
}));

const ModalTitle = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ModalContent = styled('div')((p) => ({
  marginLeft: p.theme.spacing(2),
  marginRight: p.theme.spacing(2)
}));

export const NoImportSourcesIntegrationsDialog = ({
  open,
  onClose
}: {
  open: boolean;
  onClose: () => void;
}) => {
  const { t } = useUnsafeTranslation();
  const { ROUTES } = useRoutes();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Content>
        <ModalTitle>
          <Typography component="h2" variant="h3" align="center">
            {t('No import source integration found')}
          </Typography>
        </ModalTitle>
        <ModalContent>
          <Typography component="div" variant="subtitle1" align="center">
            {t(
              'To set up an import source, start by connecting an integration under'
            )}
          </Typography>

          <MaterialUILink component={Link} to={ROUTES.org.integrations.url()}>
            <Typography component="p" align="center" variant="subtitle1">
              <b>{t('Organization → Integrations')}</b>
            </Typography>
          </MaterialUILink>
        </ModalContent>
      </Content>
    </Dialog>
  );
};

const AddIntegrationsDialog = ({
  orgId,
  open,
  onClose,
  customFields
}: {
  orgId: string;
  open: boolean;
  onClose: () => void;
  customFields: CustomUserField[];
}) => {
  const { t } = useUnsafeTranslation();
  const [dataSource, setDataSource] = useState<UserImportSourceType | null>(
    null
  );

  const [configInfosDoc] = useConfigInfos(orgId);
  const configInfo = configInfosDoc?.data;
  const { active: activeIntegrationsSet } = useIntegrationsContext();

  const activeImportIntegrations = useMemo(() => {
    return IMPORT_INTEGRATIONS.filter((i) =>
      // SFTP and email-CSV are gated only by their feature flags — they do
      // not require an existing/active integration (or any integrations
      // config) to be added.
      i.integration === Integration.SFTP ||
      i.integration === Integration.EMAIL_CSV ||
      (!!configInfo &&
        isActiveIntegration(
          configInfo,
          i.integration,
          i.mergeHRIS,
          activeIntegrationsSet
        ))
    );
  }, [
    configInfo,
    activeIntegrationsSet
  ]);

  const [
    mergeIntegration,
    setMergeIntegration
  ] = useState<MergeIntegration | null>(null);
  const close = () => {
    onClose();
    setDataSource(null);
  };

  const onIntegrationButtonClick = (
    importIntegrationInfo: ImportSourceIntegrationInfo
  ) => {
    setDataSource(importIntegrationInfo.type);
    if (
      importIntegrationInfo.type === UserImportSourceType.MERGE_HRIS &&
      importIntegrationInfo.mergeHRIS
    ) {
      setMergeIntegration(importIntegrationInfo.mergeHRIS);
    }
  };
  const importSourceIntegrationsAvailable =
    activeImportIntegrations && activeImportIntegrations.length > 0;

  return (
    <>
      {!importSourceIntegrationsAvailable && (
        <NoImportSourcesIntegrationsDialog open={open} onClose={close} />
      )}
      {importSourceIntegrationsAvailable && (
        <Dialog open={open} onClose={close} fullWidth maxWidth="md">
          <DialogTitleWithTypography>
            {t('Add User Import Integration')}
          </DialogTitleWithTypography>
          {dataSource === null && (
            <>
              <DialogContent>
                <IntegrationsGrid>
                  {activeImportIntegrations.map((x) => (
                    <IntegrationButton
                      showSatus={false}
                      key={x.type}
                      functionalities={[Functionality.IMPORT_SOURCE]}
                      name={x.name}
                      logoUrl={x.logoUrl}
                      interactive={true}
                      onClick={() => onIntegrationButtonClick(x)}
                    />
                    // <Button onClick={() => setDataSource(x.type)}>{x.name}</Button>
                  ))}
                </IntegrationsGrid>
              </DialogContent>
              <DialogActionsWithSlots
                right={
                  <Button color="primary" onClick={close}>
                    {t('Cancel')}
                  </Button>
                }
              />
            </>
          )}

          {dataSource === UserImportSourceType.AIRTABLE && (
            <AirtableSetupFlow
              orgId={orgId}
              customFields={customFields}
              onClose={close}
            />
          )}
          {dataSource === UserImportSourceType.BAMBOO_HR && (
            <BambooHrSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}
          {dataSource === UserImportSourceType.BOB && (
            <BobSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}
          {dataSource === UserImportSourceType.GOOGLE_SHEET && (
            <SpreadsheetSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
              spreadSheetType={SpreadSheetType.GOOGLE_SHEETS}
            />
          )}

          {dataSource === UserImportSourceType.MS_EXCEL && (
            <SpreadsheetSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
              spreadSheetType={SpreadSheetType.EXCEL}
            />
          )}

          {dataSource === UserImportSourceType.GREENHOUSE_V3 && (
            <GreenhouseV3SetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.PERSONIO && (
            <PersonioSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.WORKDAY_REPORT_API && (
            <WorkdaySetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}
          {dataSource === UserImportSourceType.CHARTHOP && (
            <ChartHopSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.PINGBOARD && (
            <PingboardSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.ASHBY && (
            <AshbySetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.MERGE_HRIS &&
            mergeIntegration && (
              <MergeHRISSetupFlow
                mregeIntegration={mergeIntegration as MergeHRISIntegration}
                orgId={orgId}
                onClose={close}
                customFields={customFields}
              />
            )}

          {dataSource === UserImportSourceType.ADP_WORKFORCE_NOW && (
            <AdpWorkforceNowSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.UKG && (
            <UkgSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.SFTP && (
            <SftpSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.EMAIL_CSV && (
            <EmailCsvSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}

          {dataSource === UserImportSourceType.SALESFORCE && (
            <SalesforceSetupFlow
              orgId={orgId}
              onClose={close}
              customFields={customFields}
            />
          )}
        </Dialog>
      )}
    </>
  );
};

const ImportSourceGridContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(3)
}));

const userImportAdminScheduleQuery = (orgId: string) => {
  return store()
    .collection(FS.adminSchedules)
    .where('orgId', '==', orgId)
    .where('type', '==', AdminScheduleType.USER_IMPORT);
};

const useUserImportAdminScheduleOrDefault = (
  org: IOrganization,
  sources: UserImportDataSourceIdentifier[]
) => {
  return useMappedLoadingValue(
    useCollection(userImportAdminScheduleQuery(org.id)),
    (s) => {
      if (s.empty) {
        const defaultDoc: Doc<AdminScheduleUserImport> = {
          id: shortid(),
          collection: FS.adminSchedules,
          data: {
            orgId: org.id,
            type: AdminScheduleType.USER_IMPORT,
            sources,
            frequency: {
              type: 'WEEKLY',
              weekDay: 0,
              hour: 22,
              tz: org.tz
            },
            active: false,
            createdAt: now(),
            createdBy: '',
            lastRun: null,
            nextRun: null,
            options: {
              autoApply: false,
              effectsToCommit: {
                enroll: true,
                actorChange: true,
                anchorChange: true
              }
            }
          }
        };
        return defaultDoc;
      }
      if (s.size > 1) {
        console.warn('Multiple user import schedules found', s);
      }
      const docs = sortBy(s.docs.map(toAdminScheduleDoc), (x) =>
        x.data.createdAt.toMillis()
      );
      return docs[0] as Doc<AdminScheduleUserImport>;
    }
  );
};

const ImportOptionContainer = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const ImportOptionRow = styled('div')((p) => ({
  marginLeft: p.theme.spacing(6)
}));

const ImportOption: React.FC<{
  inner?: React.ReactNode;
}> = ({ inner, children }) => {
  return (
    <ImportOptionContainer>
      <div>{children}</div>
      {inner && <ImportOptionRow>{inner}</ImportOptionRow>}
    </ImportOptionContainer>
  );
};

const SettingsDialog2 = ({
  open,
  onClose,
  org,
  ds
}: {
  open: boolean;
  onClose: () => void;
  org: IOrganization;
  ds: Doc<UserImportDataSource>[];
}) => {
  const { t } = useUnsafeTranslation();
  const theme = useTheme();
  const { ROUTES } = useRoutes();

  const [schedule, loading] = useUserImportAdminScheduleOrDefault(
    org,
    ds.map(toUserImportSourceIdentifier)
  );

  console.log(schedule);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogActionsHeader
        onClose={onClose}
        left={<Typography variant="h4">{t('Import Settings')}</Typography>}
      />

      <DialogContent>
        <FlexContainer justifyContent="space-between" marginBottom={2}>
          <Typography variant="h5">{t('Import Sources')}</Typography>
          <Link to={ROUTES.users.imports.importSources.url()}>
            <Button color="primary" startIcon={<ExternalLink size={20} />}>
              {t('Manage Import Sources')}
            </Button>
          </Link>
        </FlexContainer>
        <ImportSourceGridContainer>
          <List>
            {sortBy(
              ds.filter(
                (d) =>
                  schedule && !!schedule.data.sources.find((s) => s.id === d.id)
              ),
              (d) => d.data.createdAt.toMillis()
            ).map((d, i) =>
              d.data.type === UserImportSourceType.AIRTABLE ? (
                <ImportSourceRowAirtable
                  key={d.id}
                  d={d as Doc<UserImportDataSourceAirtable>}
                />
              ) : d.data.type === UserImportSourceType.BAMBOO_HR ? (
                <ImportSourceRowBambooHr
                  key={d.id}
                  d={d as Doc<UserImportDataSourceBambooHr>}
                />
              ) : d.data.type === UserImportSourceType.BOB ? (
                <ImportSourceRowBob
                  key={d.id}
                  d={d as Doc<UserImportDataSourceBob>}
                />
              ) : d.data.type === UserImportSourceType.GOOGLE_SHEET ? (
                <ImportSourceRowGoogleSheet
                  key={d.id}
                  d={d as Doc<UserImportDataSourceGoogleSheet>}
                />
              ) : d.data.type === UserImportSourceType.MS_EXCEL ? (
                <ImportSourceRowExcel
                  key={d.id}
                  d={d as Doc<UserImportDataSourceMSExcel>}
                />
              ) : d.data.type === UserImportSourceType.WORKDAY_REPORT_API ? (
                <ImportSourceRowWorkday
                  key={d.id}
                  d={d as Doc<UserImportDataSourceWorkdayReportApi>}
                />
              ) : d.data.type === UserImportSourceType.MERGE_HRIS ? (
                <ImportSourceRowMergeHRIS
                  key={d.id}
                  d={d as Doc<UserImportDataSourceMergeHRIS>}
                />
              ) : d.data.type === UserImportSourceType.CHARTHOP ? (
                <ImportSourceRowChartHop
                  key={d.id}
                  d={d as Doc<UserImportDataSourceChartHop>}
                />
              ) : d.data.type === UserImportSourceType.PINGBOARD ? (
                <ImportSourceRowPingboard
                  key={d.id}
                  d={d as Doc<UserImportDataSourcePingboard>}
                />
              ) : d.data.type === UserImportSourceType.ADP_WORKFORCE_NOW ? (
                <ImportSourceRowAdpWorkForceNow
                  key={d.id}
                  d={d as Doc<UserImportDataSourceAdpWorkforceNow>}
                />
              ) : d.data.type === UserImportSourceType.UKG ? (
                <ImportSourceRowUkg
                  key={d.id}
                  d={d as Doc<UserImportDataSourceUkg>}
                />
              ) : d.data.type === UserImportSourceType.ASHBY ? (
                <ImportSourceRowAshby
                  key={d.id}
                  d={d as Doc<UserImportDataSourceAshby>}
                />
              ) : d.data.type === UserImportSourceType.GREENHOUSE_V3 ? (
                <ImportSourceRowGreenhouseV3
                  key={d.id}
                  d={d as Doc<UserImportDataSourceGreenhouseV3>}
                />
              ) : d.data.type === UserImportSourceType.SFTP ? (
                <ImportSourceRowSftp
                  key={d.id}
                  d={d as Doc<UserImportDataSourceSftp>}
                />
              ) : d.data.type === UserImportSourceType.EMAIL_CSV ? (
                <ImportSourceRowEmailCsv
                  key={d.id}
                  d={d as Doc<UserImportDataSourceEmailCsv>}
                />
              ) : d.data.type === UserImportSourceType.SALESFORCE ? (
                <ImportSourceRowSalesforce
                  key={d.id}
                  d={d as Doc<UserImportDataSourceSalesforce>}
                />
              ) : null
            )}
          </List>
        </ImportSourceGridContainer>
        <FlexContainer justifyContent="space-between" marginBottom={2}>
          <Typography variant="h5">{t('Schedule Imports')}</Typography>
        </FlexContainer>

        {loading && <Loader height={150} />}
        {schedule && (
          <>
            <ImportOption
              inner={
                <FrequencyEdit
                  style={{
                    marginLeft: -theme.spacing()
                  }}
                  disabled={!schedule.data.active}
                  value={schedule.data.frequency}
                  onChange={(nextFrequencey) =>
                    updateOrCreateSchedule(schedule.id, {
                      ...schedule.data,
                      frequency: nextFrequencey
                    })
                  }
                />
              }
            >
              <FormControlLabel
                control={
                  <Switch
                    checked={schedule.data.active}
                    onChange={() =>
                      updateOrCreateSchedule(schedule.id, {
                        ...schedule.data,
                        active: !schedule.data.active
                      })
                    }
                    color="primary"
                  />
                }
                label={t('Automatically import from all sources')}
              />
            </ImportOption>
            {schedule.data.active && (
              <ImportOption
                inner={
                  <Typography variant="caption">
                    {t(
                      'If disabled, an admin will have to manually accept or reject effects'
                    )}
                  </Typography>
                }
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={!!schedule.data.options?.autoApply}
                      onChange={() =>
                        updateOrCreateSchedule(schedule.id, {
                          ...schedule.data,
                          options: {
                            ...schedule.data.options,
                            autoApply: !schedule.data.options?.autoApply
                          }
                        })
                      }
                      color="primary"
                    />
                  }
                  label={t('Apply all import effects')}
                />
              </ImportOption>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const AddImportSourceButton = ({ org }: { org: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const [ds, loading, error] = useUserImportDataSources(org.id);
  useErrorLogger('Failed to load user import data sources', error);

  const [openDialogQueryParam, setOpenDialogQueryParam] = useBooleanQueryParam(
    'addSourceOpen',
    false
  );
  const add = useDialogState(openDialogQueryParam);

  const onOpen = () => {
    setOpenDialogQueryParam(true);
    add.openDialog();
  };

  const onClose = () => {
    setOpenDialogQueryParam(false);
    add.closeDialog();
  };

  if (loading) {
    return (
      <Button>
        <ButtonLoader /> {t('Loading')}...
      </Button>
    );
  }

  if (error) {
    return null;
  }

  if (!ds) {
    return null;
  }

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        onClick={onOpen}
        startIcon={<Plus size={16} />}
      >
        {t('Add Import Source')}
      </Button>

      <AddIntegrationsDialog
        open={add.dialogOpen}
        onClose={onClose}
        orgId={org.id}
        customFields={org.customUserFields}
      />
    </>
  );
};

export const SyncButton = ({ org }: { org: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const [ds, loading, error] = useUserImportDataSources(org.id);
  useErrorLogger('Failed to load user import data sources', error);

  if (loading) {
    return (
      <Button>
        <ButtonLoader /> {t('Loading')}...
      </Button>
    );
  }

  if (error) {
    // TODO
    return null;
  }

  if (!ds) {
    return null;
  }

  return (
    <>
      <ButtonWithPromise
        pending={`${t('Starting sync')}...`}
        onClick={() =>
          // TODO - sorting of imports sources can happen here in the frontend only
          startImport(
            org.id,
            sortUserImportDataSources(ds).map(toUserImportSourceIdentifier),
            {
              ignoreMissing: true,
              deactivateInsteadOfRemove: true,
              autoEnroll: false
            }
          )
        }
        variant="contained"
        color="primary"
        startIcon={<RefreshCw size={20} />}
      >
        {t('Import now')}
      </ButtonWithPromise>
    </>
  );
};

export const SettingsButton = ({ org }: { org: IOrganization }) => {
  const { t } = useUnsafeTranslation();
  const [ds, loading, error] = useUserImportDataSources(org.id);
  useErrorLogger('Failed to load user import data sources', error);

  const settings = useDialogState();

  if (loading) {
    return (
      <Button>
        <ButtonLoader /> {t('Loading')}...
      </Button>
    );
  }

  if (error) {
    // TODO
    return null;
  }

  if (!ds) {
    return null;
  }

  return (
    <>
      <Button
        startIcon={<Settings size={20} />}
        variant="outlined"
        onClick={settings.openDialog}
      >
        {t('Import Settings')}
      </Button>

      <SettingsDialog2
        open={settings.dialogOpen}
        onClose={settings.closeDialog}
        ds={ds}
        org={org}
      />
    </>
  );
};
